// @ts-strict-ignore
import {effect, Injectable, signal} from "@angular/core";
import {AudioDevice, IAudioClientService} from "./i-audio-client.service";
import {NetworkAudioInputChannel} from "@models";
import Enumerable from "linq";
import {SimConfigurationService} from "../sim-configuration.service";
import {environment} from "../../../../../environments/environment";
import {SimUtilityService} from "../sim-utility.service";
import {UntilDestroy} from "@ngneat/until-destroy";
import {AudioWebsocketSenderClientParams} from "../workers/audio-client-websocket.worker";

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class BrowserAudioClientService implements IAudioClientService {
	public readonly connected = signal<boolean>(false);
	public readonly selectedAudioDevice = signal<AudioDevice>(null);
	public readonly audioDevices = signal<AudioDevice[]>([]);
	public readonly muted = signal<boolean>(false);
	public readonly volume = signal<number>(1);
	public readonly supportsVisualizer = true;
	public readonly supportsVolumeControl = true;

	private selectedAudioDeviceIsUserActivated = false;

	private canvas: HTMLCanvasElement = null;
	private audioContext: AudioContext = null;
	private mediaSource: MediaStreamAudioSourceNode = null;
	private audioGainNode: GainNode = null;
	private mediaSourceDevice: AudioDevice = null;
	private analyserNode: AnalyserNode = null;
	private audioWorkletNode: AudioWorkletNode = null;
	private webSocketWorker: Worker = null;

	private currentAudioInputChannel: NetworkAudioInputChannel = null;

	private currentVisualizerId: string = null;
	private sendingStarted: boolean = false;
	private visualizerStarted: boolean = false;

	constructor() {
		effect(() => {
			this.applyAudioGain();
		});

		navigator.mediaDevices.ondevicechange = async () => {
			console.debug("Device change detected.");
			await this.deviceChanged();
		};
		this.checkConnection().then();
	}

	private async deviceChanged() {
		await this.reloadAudioDevices();
		if (this.selectedAudioDevice()) {
			//is the selected device still available?
			let currentDevice = this.audioDevices().find((device) => device.deviceId === this.selectedAudioDevice()?.deviceId);

			if (!this.selectedAudioDeviceIsUserActivated && !currentDevice?.isDefault) {
				//if the selected device was not user activated and is not the default device, try to find the default device
				currentDevice = this.audioDevices().find((device) => device.isDefault);
			} else {
				//else wait for the current device to be available
				//and only reset the worker nodes
			}

			if (currentDevice) {
				await this.setAudioDeviceInternal(currentDevice, this.selectedAudioDeviceIsUserActivated);
			} else {
				await this.resetAllAudioWorkersAndNodes();
			}
		}
	}

	private async checkConnection(): Promise<void> {
		// Request permission to access audio devices
		await navigator.mediaDevices.getUserMedia({audio: true, video: false});

		await this.reloadAudioDevices();
		this.connected.set(this.audioDevices().length > 0);
	}

	async reloadAudioDevices(): Promise<void> {
		const devices = await navigator.mediaDevices.enumerateDevices();
		const audioInputDevices = devices.filter((device) => device.kind === "audioinput");
		const allAudioInputDevices = audioInputDevices.map((device, index) => {
			return new AudioDevice(device.deviceId, device.groupId, device.label || `Microphone ${index + 1}`);
		});
		//chrome is returning the same device multiple times - with default und "communications" label
		//https://stackoverflow.com/questions/53304934/why-does-mediadevices-enumeratedevices-list-some-devices-twice-what-is-default
		const filteredAudioInputDevices = Enumerable.from(allAudioInputDevices)
			.groupBy((x) => x.groupId)
			.select((x) => {
				const device = x.first((x) => x.deviceId != "default" && x.deviceId != "communications");
				device.isDefault = x.any((x) => x.deviceId == "default");
				return device;
			})
			.toArray();

		this.audioDevices.set(filteredAudioInputDevices);

		if (!this.selectedAudioDevice()) {
			//choose default audio device
			await this.setAudioDeviceInternal(
				this.audioDevices().find((device) => device.isDefault),
				false,
			);
		}
	}

	private async setAudioDeviceInternal(audioDevice: AudioDevice, userActivated: boolean): Promise<boolean> {
		if (!audioDevice) return false;
		if (this.selectedAudioDevice()?.isSameAs(audioDevice) && this.mediaSource) return false;

		this.selectedAudioDevice.set(audioDevice);
		this.selectedAudioDeviceIsUserActivated = userActivated;
		await this.resetAllAudioWorkersAndNodes();

		if (this.sendingStarted) {
			await this.startSending(this.currentAudioInputChannel);
		}

		if (this.visualizerStarted) {
			await this.startVisualizer(this.canvas);
		}

		return true;
	}

	async setAudioDevice(audioDevice: AudioDevice): Promise<boolean> {
		return this.setAudioDeviceInternal(audioDevice, true);
	}

	async startVisualizer(canvas: HTMLCanvasElement): Promise<boolean> {
		console.debug("Starting audio visualizer");
		try {
			if (!this.supportsVisualizer) {
				console.error("Visualizer not supported");
				return false;
			}
			if (!canvas) {
				console.error("No canvas provided");
				return false;
			}

			await this.checkConnection();

			this.canvas = canvas;

			const success = await this.initAudioContext();
			if (!success) {
				return false;
			}

			// Create an AnalyserNode for audio level visualization
			this.analyserNode = this.audioContext.createAnalyser();
			// Lower fftSize for smoother visual output
			this.analyserNode.fftSize = 512;
			// AudioGainNode to AnalyserNode
			this.audioGainNode.connect(this.analyserNode);

			// Start visualizing the audio level
			this.visualizeAudioLevel();

			return true;
		} catch (error) {
			console.error("Error accessing media devices or audio context", error);
			return false;
		}
	}

	async stopVisualizer(): Promise<boolean> {
		console.debug("Stopping audio visualizer");
		this.visualizerStarted = false;
		await this.resetAudioVisualizerNode();
		return true;
	}

	async startSending(audioInputChannel: NetworkAudioInputChannel): Promise<boolean> {
		try {
			console.debug("Starting audio sending", audioInputChannel);

			if (!audioInputChannel) {
				console.error("No audio input channel provided");
				return false;
			}

			await this.checkConnection();

			const success = await this.initAudioContext();
			if (!success) {
				return false;
			}

			this.sendingStarted = true;
			this.currentAudioInputChannel = audioInputChannel;

			const params: AudioWebsocketSenderClientParams = {
				wsUrl: SimConfigurationService.settings.websocketAudioInEndpoint,
				clientId: this.selectedAudioDevice().label,
				networkAudioInputChannelId: audioInputChannel.id,
				bitDepth: audioInputChannel.bitDepth,
				useCompression: audioInputChannel.compression,
				bufferLengthMultiplier: audioInputChannel.bufferLengthMultiplier,
			};

			//message channel to communicate between worker and audio worklet
			const audioMessageChannel = new MessageChannel();

			// Load the worker
			this.webSocketWorker = new Worker(new URL("../workers/audio-client-websocket.worker.ts", import.meta.url), {type: "module"});

			// Load the audio worklet processor
			await this.audioContext.audioWorklet
				.addModule(environment.baseHref + "/worklets/audio-client-worklet.js")
				.catch((error) => console.error(error));

			// Create an AudioWorkletNode
			this.audioWorkletNode = new AudioWorkletNode(this.audioContext, "audio-client-worklet", {
				processorOptions: {
					bitDepth: params.bitDepth,
					useCompression: params.useCompression,
				},
			});

			// Connect the worker and the audio worklet with the message channel
			this.webSocketWorker.postMessage({name: "port"}, [audioMessageChannel.port1]);
			this.audioWorkletNode.port.postMessage({name: "port"}, [audioMessageChannel.port2]);

			//start webWorker
			this.webSocketWorker.postMessage({
				name: "start",
				params: params,
			});

			// AudioGainNode to AudioWorkletNode
			this.audioGainNode.connect(this.audioWorkletNode);

			return true;
		} catch (error) {
			console.error("Error startSendingAudio", error);
			return false;
		}
	}

	async stopSending(): Promise<boolean> {
		console.debug("Stopping audio sending");
		this.sendingStarted = false;
		await this.resetAudioSendWorker();
		return true;
	}

	private applyAudioGain() {
		const value = this.muted() ? 0 : this.volume();
		if (this.audioGainNode) {
			this.audioGainNode.gain.value = value;
		}
	}

	private async initAudioContext(): Promise<boolean> {
		if (!this.selectedAudioDevice()) {
			console.error("No audio device selected");
			return false;
		}

		if (!this.mediaSource || !this.mediaSourceDevice?.isSameAs(this.selectedAudioDevice())) {
			// Initialize audio context and settings
			await this.resetAllAudioWorkersAndNodes();

			console.debug("Initializing audio context with:", this.selectedAudioDevice());
			const mediaStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					deviceId: this.selectedAudioDevice().deviceId,
					groupId: this.selectedAudioDevice().groupId,
					//this is necessary to avoid browser specific audio processing
					echoCancellation: false,
					noiseSuppression: false,
					autoGainControl: false,
				},
			});

			this.audioContext = new AudioContext({
				latencyHint: "interactive",
				sampleRate: 48000,
			});

			if (this.audioContext.state === "suspended") {
				await this.audioContext.resume();
			}

			this.mediaSourceDevice = this.selectedAudioDevice();
			this.mediaSource = this.audioContext.createMediaStreamSource(mediaStream);

			this.audioGainNode = this.audioContext.createGain();
			this.mediaSource.connect(this.audioGainNode);
			this.applyAudioGain();
		}

		return true;
	}

	private async resetAudioVisualizerNode() {
		try {
			if (this.analyserNode) {
				this.analyserNode.disconnect();
				this.analyserNode = null;
			}

			if (!this.sendingStarted) {
				await this.resetAudioContext();
			}
		} catch (e) {
			console.error("Error at resetAudio", e);
		}
	}

	private async resetAudioSendWorker() {
		try {
			if (this.audioWorkletNode) {
				this.audioWorkletNode.disconnect();
				this.audioWorkletNode.port.postMessage({name: "stop"});
				this.audioWorkletNode = null;
			}

			if (this.webSocketWorker) {
				this.webSocketWorker.postMessage({name: "stop"});
				this.webSocketWorker.terminate();
				this.webSocketWorker = null;
			}

			if (!this.visualizerStarted) {
				await this.resetAudioContext();
			}
		} catch (e) {
			console.error("Error at resetAudioSendWorker", e);
		}
	}

	private async resetAudioContext() {
		try {
			if (this.audioGainNode) {
				this.audioGainNode.disconnect();
				this.audioGainNode = null;
			}

			if (this.mediaSource) {
				this.mediaSource.disconnect();
				this.mediaSource = null;
				this.mediaSourceDevice = null;
			}

			if (this.audioContext) {
				await this.audioContext.close();
				this.audioContext = null;
			}
		} catch (e) {
			console.error("Error at resetAudioContext", e);
		}
	}

	private async resetAllAudioWorkersAndNodes() {
		await this.resetAudioVisualizerNode();
		await this.resetAudioSendWorker();
		await this.resetAudioContext();
	}

	private visualizeAudioLevel() {
		const visualizerId = SimUtilityService.createGuid();
		const canvasCtx = this.canvas.getContext("2d");
		const WIDTH = this.canvas.width;
		const HEIGHT = this.canvas.height;

		//const dataArray = new Uint8Array(analyser.fftSize);
		const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);
		const analyserNode = this.analyserNode;

		let draw = () => {
			try {
				canvasCtx.clearRect(0, 0, WIDTH, HEIGHT); // Clear the canvas

				if (this.currentVisualizerId !== visualizerId || !this.visualizerStarted) {
					console.debug("Stopping visualization", visualizerId);
					return;
				}

				analyserNode.getByteFrequencyData(dataArray);

				const barWidth = (WIDTH / dataArray.length) * 2.5; // Width of each bar
				let barHeight: number;
				let x = 0;

				for (let i = 0; i < dataArray.length; i++) {
					barHeight = (dataArray[i] / 255) * HEIGHT; // Scale the height

					canvasCtx.fillStyle = "rgb(200,200,200)"; // `rgb(${255 * (barHeight / HEIGHT)}, 25, 25)`;  // Color the bars
					canvasCtx.fillRect(x, HEIGHT, barWidth, -barHeight);

					x += barWidth + 1; // Move to the next bar
				}

				requestAnimationFrame(draw); // Loop the drawing function for continuous visualization
			} catch (e) {
				console.error("Error drawing audio level", visualizerId, e);
			}
		};

		this.currentVisualizerId = visualizerId;
		this.visualizerStarted = true;
		draw();
	}
}
